import { Fragment, type ReactNode } from "react";
import type { LegalBlock } from "@keydials/api-client";

/**
 * Renders a legal document's blocks (KEY-618). The text carries only `**bold**`, `*italic*` and
 * `[label](href)`, parsed here into elements — no HTML is injected, so nothing in a document can run.
 * Links to "/…" are pages on the marketing site and open there, in a new tab, so a buyer reading the
 * terms mid-purchase does not lose the page they were on.
 */
export function LegalText({ blocks, siteUrl }: { blocks: readonly LegalBlock[]; siteUrl: string }) {
  return (
    <>
      {blocks.map((block, i) => {
        if ("h" in block) return <h3 key={i}>{inline(block.h, siteUrl)}</h3>;
        if ("h3" in block) return <h4 key={i}>{inline(block.h3, siteUrl)}</h4>;
        if ("p" in block) return <p key={i}>{inline(block.p, siteUrl)}</p>;
        if ("ul" in block) return <ul key={i}>{block.ul.map((item, j) => <li key={j}>{inline(item, siteUrl)}</li>)}</ul>;
        if ("ol" in block) return <ol key={i}>{block.ol.map((item, j) => <li key={j}>{inline(item, siteUrl)}</li>)}</ol>;
        if ("table" in block)
          return (
            <table key={i}>
              <thead>
                <tr>{block.table.head.map((cell, j) => <th key={j}>{inline(cell, siteUrl)}</th>)}</tr>
              </thead>
              <tbody>
                {block.table.rows.map((row, j) => (
                  <tr key={j}>{row.map((cell, k) => <td key={k}>{inline(cell, siteUrl)}</td>)}</tr>
                ))}
              </tbody>
            </table>
          );
        return null;
      })}
    </>
  );
}

const TOKEN = /\*\*(.+?)\*\*|\*(.+?)\*|\[([^\]]+)\]\(([^)\s]+)\)/g;

/** `**b**`, `*i*` and `[label](href)` → elements. Anything else is plain text. */
export function inline(text: string, siteUrl: string): ReactNode {
  const out: ReactNode[] = [];
  let last = 0;
  for (const m of text.matchAll(TOKEN)) {
    const at = m.index ?? 0;
    if (at > last) out.push(text.slice(last, at));
    if (m[1] !== undefined) out.push(<strong key={at}>{inline(m[1], siteUrl)}</strong>);
    else if (m[2] !== undefined) out.push(<em key={at}>{inline(m[2], siteUrl)}</em>);
    else out.push(<a key={at} href={href(m[4], siteUrl)} target="_blank" rel="noreferrer">{m[3]}</a>);
    last = at + m[0].length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out.length === 1 ? out[0] : <Fragment>{out}</Fragment>;
}

/** Only site paths, mail and web links; anything else is dropped to a harmless "#". */
function href(raw: string, siteUrl: string): string {
  if (raw.startsWith("/")) return `${siteUrl}${raw}`;
  if (/^(https?:|mailto:)/i.test(raw)) return raw;
  return "#";
}
